import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = ({ cart, onClearCart }) => {
  const navigate = useNavigate();
  const { user, authHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({ name: user.name || '', email: user.email || '' });
    }
  }, [user]);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = total >= 500 ? 0 : 50;
  const finalTotal = total + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        })),
        customer_name: formData.name,
        customer_email: formData.email
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData, { headers: authHeaders() });
      const order = orderResponse.data;

      const checkoutData = {
        order_id: order.id,
        origin_url: window.location.origin
      };

      const sessionResponse = await axios.post(`${API}/checkout/session`, checkoutData);
      
      if (sessionResponse.data.url) {
        window.location.href = sessionResponse.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erreur lors de la création de la commande');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/panier');
    return null;
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-8" data-testid="checkout-title">
          Finaliser la Commande
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 space-y-6" data-testid="checkout-form">
              <div>
                <h2 className="font-heading font-semibold text-2xl mb-6">Informations de Livraison</h2>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="name">
                  Nom complet *
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                  placeholder="Jean Dupont"
                  required
                  data-testid="checkout-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="email">
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                  placeholder="jean.dupont@example.com"
                  required
                  data-testid="checkout-email-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-accent/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="proceed-payment-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  'Procéder au paiement'
                )}
              </button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 sticky top-24" data-testid="checkout-summary">
              <h3 className="font-heading font-semibold text-xl mb-6">Résumé de la Commande</h3>
              
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm" data-testid={`checkout-item-${item.id}`}>
                    <span className="text-muted-foreground">
                      {item.name} x{item.quantity}
                    </span>
                    <span className="font-medium">{(item.price * item.quantity).toFixed(2)} DH</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span className="font-medium">{total.toFixed(2)} DH</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Livraison</span>
                  <span className="font-medium">{shipping === 0 ? 'Gratuite' : `${shipping.toFixed(2)} DH`}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold text-primary" data-testid="checkout-total">
                    {finalTotal.toFixed(2)} DH
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};